import get from 'lodash/get';

import {fetchDataForNode} from 'src/graph';
import {FlatGraph} from 'src/state/dependency2';
import {isSearchNode, isNode, isLink} from 'src/util/gojs';
import go, {
  Key,
  InputEvent,
  GraphObject,
  Node,
  Part,
} from 'src/util/gojs/go';
import {
  makeNodeTemplate,
  makeLinkTemplate,
  makeDiagram,
  commitNodesToDiagram,
} from 'src/graph';

export const unselect = (p: Part) => p.isSelected = false;
export const select = (p: Part | Node) => p.isSelected = true;

export const allChildrenRemoved = (node: Node) => node.findNodesOutOf().count === 0;

export const deselectAllNodes = (node: Node) => {
  const diagram = node.diagram;
  if (diagram) {
    node.diagram.selection.each(unselect);
  }
};

export const replaceSelection = (node: Node) => {
  deselectAllNodes(node);
  node.isSelected = true;
};

const recursiveOperation = (operation: (node: Node) => void) => (node: Node, predicateFn: (n: Node) => boolean = () => true) => {
  function op(node: Node) {
    if (!node) {
      return;
    }
    const iterator = node.findLinksOutOf().filter((link) => {
      return link.data.contains || link.data.groups;
    }).map((x) => x.toNode);

    if (iterator.count === 0) {
      return;
    }


    iterator.each((n) => {
      if (!n) {
        return;
      }
      op(n);

      if (predicateFn && !predicateFn(n)) {
        return;
      }
      operation(n);
    });
  }

  op(node);

  if (!predicateFn(node)) {
    return;
  }

  operation(node);
};

export const hideChildren = recursiveOperation((node) => {
  const parentNode = node.findTreeParentNode();
  const diagram = node.diagram;

  if (!diagram) {
    return;
  }


  diagram.removeParts(node.findLinksInto());

  if (parentNode) {
    if (allChildrenRemoved(parentNode)) {
      parentNode.collapseTree();
      parentNode.data.wasLoaded = false;
    }
  }

  node.findNodesOutOf().each((n) => {
    if (n && n.findLinksInto().filter((l) => l.fromNode?.key !== node.key).count === 0) {
      // guessing there maybe a gotcha here too
      diagram.removeParts(n.findTreeChildrenNodes());
      diagram.remove(n);
    }
  });

  diagram.remove(node);
});

export const selectNodeAndChildren = recursiveOperation(select);

export const makeNodeMetaDataGetter = <T>(key: string, defaultValue?: T) => (node: Node) => get(node, ['data', 'metadata', key], defaultValue);

export const getNodeIdentity = makeNodeMetaDataGetter('identity');
export const getNodeType = makeNodeMetaDataGetter('type');

const makeClickToggleNodeExpansion = (
    flatGraph: FlatGraph,
    setCurrNode: (_: any) => void,
    setSkipModelUpdate: (_: boolean) => void,
    handleSelects: (method: string, data: any) => void,
) => async (_e: InputEvent, obj: GraphObject) => {
  const node = obj.part;

  if (!isNode(node)) {
    return;
  }

  await toggleNodeExpansion(
      node,
      flatGraph,
      setCurrNode,
      setSkipModelUpdate,
      handleSelects,
  );
};

export async function toggleNodeExpansion(
    node: Node,
    flatGraph: FlatGraph,
    setCurrNode: (_: any) => void,
    setSkipModelUpdate: (_: boolean) => void,
    handleSelects: (m: string, _: any) => void,
) {
  setSkipModelUpdate(true);
  const data = node.data;
  replaceSelection(node);

  if (!node.diagram) {
    return;
  }

  const {
    key: id,
    text: name,
    metadata,
  } = (data || {});

  if (!data.wasLoaded) {
    try {
      const {nodes, edges} = await fetchDataForNode(node);
      flatGraph.nodes = nodes;
      flatGraph.edges = edges;
      setCurrNode({id, name, metadata});
    } catch (e) {
      return;
    }
  }
  node.diagram.commit(async (diagram) => {
    const {category} = node;
    if (category === 'parent') {
      if (!data.wasLoaded) {
        data.wasLoaded = true;

        const {nodes, edges} = flatGraph;

        nodes.forEach((node) => {
          flatGraph.showNode(node.key);
        });

        handleSelects('update', {list: nodes, hiddenMap: flatGraph.hiddenNodesMap});

        commitNodesToDiagram(diagram, {nodes: nodes, edges});
        node.isTreeExpanded = true;

        const newIds = flatGraph.nodes.map(({id}) => id) ?? [];
        const newToIds = flatGraph.edges.map(({to}) => to) ?? [];
        const disjoint = newToIds.filter((x) => !newIds.includes(x));

        disjoint?.forEach((id) => {
          const missingNode = diagram.findNodeForKey(id);
          if (missingNode) {
            missingNode.findTreeParentChain().each((p) => {
              if (isNode(p)) {
                if (!p.isTreeExpanded && p.data.wasLoaded) {
                  p.expandTree();
                }
                // Removed code that was added in
              }
            });
          }
        });
      } else {
        if (node.isTreeExpanded) {
          node.collapseTree();
          const it = node.findTreeChildrenNodes();
          const nodes: Array<string> = [];
          while (it.next()) {
            const item = it.value;
            flatGraph.hideNode('' + item.key);
            nodes.push('' + it.value.key);
          }
          handleSelects('collapse', {hiddenMap: flatGraph.hiddenNodesMap, nodeIds: nodes});
        } else {
          node.expandTree();
          const it = node.findTreeChildrenNodes();
          const nodes: Array<string> = [];
          while (it.next()) {
            const item = it.value;
            flatGraph.showNode('' + item.key);
            nodes.push('' + it.value.key);
          }
          handleSelects('expand', {hiddenMap: flatGraph.hiddenNodesMap, nodeIds: nodes});
        }
      }
    }

    // this triggers the selection changed which must be done once all new nodes are added.
    // or the link labels wont be drawn
    diagram.selectCollection([node]);
  });
}

const clickToggleNodeSelection = (_e: InputEvent, obj: GraphObject) => {
  const node = obj.part;

  if (!isNode(node)) {
    return;
  }

  selectNodeAndChildren(node);
};


export const initDiagram = ({
  flatGraph,
  setIsShowContextMenuParent,
  showContextMenu,
  setContextNodeKey,
  setSkipModelUpdate,
  setCurrNode,
  handleSelects = () => {},
  setContextLinkKey = () => {},
}: {
  flatGraph: FlatGraph,
  setIsShowContextMenuParent: (_: boolean) => void,
  showContextMenu: (x: number, y:number) => void,
  setContextNodeKey: (_: Key) => void,
  setSkipModelUpdate: (_: boolean) => void,
  setCurrNode: (data: any) => void,
  handleSelects?: (method: string, data: any) => void,
  setContextLinkKey?: (_: Key) => void,
}) => {
  async function prepareAndShowContextMenu(e: InputEvent, obj: GraphObject) {
    if (!isNode(obj)) {
      return;
    }

    if (!isSearchNode(obj.data)) {
      const {nodes: untrackedNodes, edges} = await fetchDataForNode(obj);
      // TODO: This seems redundant with the assignment of flatGraph just
      // after this function (fetchDataForNode) is invoked: Lines 168, 159.  Possibly others.
      flatGraph.nodes = untrackedNodes;
      flatGraph.edges = edges;
    }
    setIsShowContextMenuParent(
      obj?.category === 'parent' ? true : false,
    );

    const viewPoint = e.viewPoint;
    showContextMenu(viewPoint.x, viewPoint.y);

    setContextNodeKey(obj.key);
    setContextLinkKey(undefined);
  }
  async function prepareAndShowLinkContextMenu(e: InputEvent, obj: GraphObject) {
    if (!isLink(obj)) {
      return;
    }

    const viewPoint = e.viewPoint;
    showContextMenu(viewPoint.x, viewPoint.y);

    setContextNodeKey(undefined);
    setContextLinkKey(obj.key);
  }

  const diagram = makeDiagram({
    handleContextClick: (e) => {
      const viewPoint = e.viewPoint;
      showContextMenu(viewPoint.x, viewPoint.y);

      setContextNodeKey(undefined);
      setContextLinkKey(undefined);
    },
  });

  diagram.nodeTemplateMap = new go.Map<string, go.Node>()
      .add('', makeNodeTemplate({
        expandable: true,
        handleClickExpander: makeClickToggleNodeExpansion(
            flatGraph,
            setCurrNode,
            setSkipModelUpdate,
            handleSelects,
        ),
        handleDoubleClick: clickToggleNodeSelection,
        handleContextClick: prepareAndShowContextMenu,
      }))
      .add('impact', makeNodeTemplate({
        expandable: false,
        handleClickExpander: makeClickToggleNodeExpansion(
            flatGraph,
            setCurrNode,
            setSkipModelUpdate,
            handleSelects,
        ),
        handleDoubleClick: clickToggleNodeSelection,
        handleContextClick: prepareAndShowContextMenu,
      }));

  diagram.linkTemplate = makeLinkTemplate(
      {handleContextClick: prepareAndShowLinkContextMenu},
  );

  diagram.commit((d) => {
    d.model.addNodeDataCollection(flatGraph.nodes ?? []);
  });

  diagram.addDiagramListener('LayoutCompleted', (_e) => {
    const selected = diagram.selection.first();
    if (selected) {
      diagram.scrollToRect(selected.actualBounds);
    }
  });

  diagram.addDiagramListener('ObjectSingleClicked', async function(e) {
    const part = e.subject.part;
    if (!(part instanceof go.Link)) {
      const selnode = diagram.selection.first();
      if (!(selnode instanceof go.Node)) {
        return;
      }

      setSkipModelUpdate(true);
      const name = selnode.data.text;
      const metadata = selnode.data.metadata;

      setCurrNode({...selnode.data, name, metadata});
    }
  });

  diagram.addDiagramListener('ChangedSelection', (e) => {
    const selection = diagram.selection.first();
    diagram.commit((d) => {
      diagram.links.each((l) => d.model.set(l.data, 'neighborSelected', false));
      if (selection instanceof go.Node) {
        selection.findLinksConnected().each((l) => {
          d.model.set(l.data, 'neighborSelected', true);
        });
      }
    }, 'ChangedSelection',
    );
  });

  return diagram;
};
